// The words a season is told in, shared by a team's wrap (`wrap.ts`) and the
// league's yearbook (`yearbook.ts`) so the two never say a score, a place or
// a game's winner differently. Pure.

import { formatValue, type MatchupFact } from '@fsp/awards';

export function pts(n: number): string {
  return formatValue(n, 'points');
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

const TIMES = ['', 'once', 'twice', 'three times', 'four times', 'five times'];
export const times = (n: number) => TIMES[n] ?? `${n} times`;

export function recordOf(w: number, l: number, t: number): string {
  return t > 0 ? `${w}–${l}–${t}` : `${w}–${l}`;
}

/** "Alpha’s", and "Walk Wolves’" for a name that ends in an s. */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`;
}

/** "A, B and C". */
export function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Who a final game went to: as the scorer settled it, else by the scores,
 * a level bracket game going to the home side, the higher seed, as
 * `MatchupFact` says.
 */
export function winnerOf(m: MatchupFact): string | null {
  if (m.winnerTeamId !== undefined) return m.winnerTeamId;
  if (m.homeScore === m.awayScore) return m.kind === 'regular' ? null : m.homeTeamId;
  return m.homeScore > m.awayScore ? m.homeTeamId : m.awayTeamId;
}

export function sideOf(m: MatchupFact, teamId: string) {
  const home = m.homeTeamId === teamId;
  return {
    score: home ? m.homeScore : m.awayScore,
    against: home ? m.awayScore : m.homeScore,
    opponent: home ? m.awayTeamId : m.homeTeamId,
  };
}
